/**
 * «Удобный выбор времени» (04.11.2026): the rules of the date-and-time field without React — how a typed time is read,
 * which times are offered inside the working hours of a warehouse and what UTC offset a zone has. The server applies
 * the same working-hours rule to the free windows of the gates (`documents/schedule.working_intervals`).
 */

const DAY = 24 * 60;

// «9», «09», «930», «0930», «9:30», «9.30», «9 30» — hours, then optionally two digits of minutes
const CLOCK = /^(\d{1,2})(?:[:.\-\s]?(\d{2}))?$/;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** «14» → «14:00», «1430» → «14:30», «9.5» → null: a time is hours and, if given, two digits of minutes. */
export function parseTime(text: string): string | null {
  const match = CLOCK.exec(text.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  if (hours > 23 || minutes > 59) return null;
  return `${pad(hours)}:${pad(minutes)}`;
}

export function minutesOf(clock: string): number {
  const [hours, minutes] = clock.split(":");
  return Number(hours) * 60 + Number(minutes ?? 0);
}

export function clockOf(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** The working minutes of a day: «08:00–20:00» → [[480, 1200]]; a night shift «20:00–08:00» → the morning and the evening. */
export function workingIntervals(from?: string | null, to?: string | null): [number, number][] {
  const start = from ? minutesOf(from) : null;
  const finish = to ? minutesOf(to) : null;
  if ((start === null && finish === null) || start === finish) return [[0, DAY]];
  const a = start ?? 0;
  const b = finish ?? DAY;
  return a < b ? [[a, b]] : [[0, b], [a, DAY]];
}

/** Whether the warehouse works at this time; without working hours every time is inside. */
export function insideWork(clock: string, from?: string | null, to?: string | null): boolean {
  const at = minutesOf(clock);
  return workingIntervals(from, to).some(([a, b]) => at >= a && at < b);
}

/** Times offered in the list: every `step` minutes inside the working hours, in the order of the day. */
export function timeOptions(from?: string | null, to?: string | null, step = 30): string[] {
  const out: string[] = [];
  for (const [a, b] of workingIntervals(from, to)) {
    // a shift that starts off the grid («08:15») offers its own start too
    if (a % step !== 0) out.push(clockOf(a));
    for (let at = Math.ceil(a / step) * step; at < b; at += step) out.push(clockOf(at));
  }
  return [...new Set(out)].sort((x, y) => minutesOf(x) - minutesOf(y));
}

/** Whether a listed time answers what is being typed: «9» and «09» → 09:00, 09:30; «93» or «930» → 09:30; «14» → 14:…. */
export function matchesTyped(option: string, typed: string): boolean {
  const digits = typed.replace(/\D/g, "");
  if (!digits) return true;
  const own = option.replace(":", "");
  const candidates = [digits, digits.padStart(2, "0"), digits.length === 3 ? digits.padStart(4, "0") : digits];
  return candidates.some((c) => own.startsWith(c));
}

/** The words of the working hours: «08:00–20:00», a night shift «20:00–08:00», nothing for round the clock. */
export function workWords(from?: string | null, to?: string | null): string | null {
  if (!from && !to) return null;
  return `${from ?? "00:00"}–${to ?? "24:00"}`;
}

/** The interval of `busy` that holds this time («10:00–11:00»), if any. */
export function busyAt(clock: string, busy: { from: string; to: string }[]): { from: string; to: string } | null {
  const at = minutesOf(clock);
  return busy.find((b) => at >= minutesOf(b.from) && at < minutesOf(b.to)) ?? null;
}

/** «UTC+5», «UTC−3:30» — the offset of a zone at a moment, read by the browser's own time zone database. */
export function zoneOffset(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(at);
    const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    const offset = name.replace(/^GMT/, "");
    return offset ? `UTC${offset.replace("-", "−")}` : "UTC+0";
  } catch {
    return zone;
  }
}

/** The time zone of this device («Asia/Almaty»). */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
